import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { useOrg } from '@/contexts/OrgContext';
import { useAssociations } from '@/lib/crm/hooks/useAssociations';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { apiPatch } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { quickEmailProposal } from '@/lib/proposals/core/ActionBuilder';
import { useCreateProposal } from '@/lib/proposals/query/mutations';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { ResearchDetail } from '../../record/research/ResearchDetail';
import { extractResearchData } from '../../record/research/useResearch';
import { CollapsibleSection } from './collapsible-section';

interface ResearchBriefSectionProps {
  object: CrmObject;
}

function hasNonEmptyEmail(obj: CrmObject): boolean {
  const email = getPropertyValue<string | string[]>(obj.properties?.ahoy_email);
  if (Array.isArray(email)) {
    return email.some(e => typeof e === 'string' && e.trim().length > 0);
  }
  return typeof email === 'string' && email.trim().length > 0;
}

export function ResearchBriefSection({ object }: ResearchBriefSectionProps) {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const createProposal = useCreateProposal(object.org_id);
  const { openProposal } = useProposalSidebar();
  const queryClient = useQueryClient();

  const research = useMemo(() => extractResearchData(object), [object]);

  // Stable references: a fresh `?? []` array and inline options object would
  // give useAssociations new inputs every render, recomputing everything
  // downstream of resolvedAssociations (FRONTEND_PATTERNS § Stable References).
  const associationEdges = useMemo(
    () => object.associations ?? [],
    [object.associations]
  );
  const associationOptions = useMemo(
    () => ({ sourceEntityTypeName: object.entity_type_name }),
    [object.entity_type_name]
  );

  const { associations: resolvedAssociations } = useAssociations(
    object.org_id,
    associationEdges,
    associationOptions
  );

  const { contactEmail, contactEntityId, recordType } = useMemo(() => {
    const pool = new Map<string, CrmObject>();
    for (const a of resolvedAssociations) {
      if (a.object !== null) {
        pool.set(a.object.entity_id, a.object);
      }
    }
    for (const [id, obj] of Object.entries(object.associated_objects ?? {})) {
      if (!pool.has(id)) {
        pool.set(id, obj);
      }
    }

    const poolValues = Array.from(pool.values());
    const contactEntity = poolValues.find(
      obj => obj.entity_type_name === 'ahoy_contact' && hasNonEmptyEmail(obj)
    );
    const companyEntity = contactEntity
      ? undefined
      : poolValues.find(
          obj =>
            obj.entity_type_name === 'ahoy_company' && hasNonEmptyEmail(obj)
        );

    const foundEntity = contactEntity ?? companyEntity;
    if (!foundEntity) {
      return {
        contactEmail: undefined,
        contactEntityId: undefined,
        recordType: undefined,
      };
    }

    const emailRaw = getPropertyValue<string | string[]>(
      foundEntity.properties?.ahoy_email
    );
    const isUsableEmail = (e: unknown): e is string =>
      typeof e === 'string' && e.trim().length > 0;
    let contactEmail: string | undefined;
    if (Array.isArray(emailRaw)) {
      contactEmail = emailRaw.find(isUsableEmail)?.trim();
    } else if (isUsableEmail(emailRaw)) {
      contactEmail = emailRaw.trim();
    }
    const contactEntityId = foundEntity.entity_id;
    const recordType = contactEntity ? 'contact' : 'company';

    return { contactEmail, contactEntityId, recordType };
  }, [object.associated_objects, resolvedAssociations]);

  // Note: sourceTypeName is dynamic (ahoy_${recordType}_research) and sourceId prefers
  // the contact entity ID when available. This intentionally differs from EmbeddedResearchBrief
  // in ExternalActionCard, which only has the research entityId in scope (no recordType or
  // contactEntityId). Do not attempt to unify these without considering both surfaces.
  const handleComposeEmail = useCallback(
    async (data: {
      component_id: string;
      subject: string;
      body: string;
      context_label: string;
    }) => {
      if (!contactEmail || !user?.id) {
        return;
      }
      if (!data.subject?.trim() || !data.body?.trim()) {
        toast.error('Conversation starter is missing subject or body content');
        return;
      }
      try {
        const request = quickEmailProposal({
          title: `Email: ${data.subject}`,
          description: `Conversation starter from research: ${data.context_label}`,
          assignedUserId: user.id,
          sourceTypeName: `ahoy_${recordType}_research`,
          sourceId: contactEntityId || object.entity_id,
          sourceContext: {
            channel: 'ahoy_research_starter',
            account_identifier: user.email,
          },
          to: [contactEmail],
          subject: data.subject,
          body: data.body,
        });
        const proposal = await createProposal.mutateAsync(request);
        openProposal(proposal.id);
        try {
          await apiPatch(
            `/api/orgs/${object.org_id}/research/${object.entity_id}/components/${data.component_id}/`,
            { proposal_id: proposal.id }
          );
        } catch (patchError) {
          logger.error(
            'Failed to store proposal_id on conversation starter component',
            patchError instanceof Error ? patchError : undefined,
            {
              entity_id: object.entity_id,
              component_id: data.component_id,
              proposal_id: proposal.id,
            }
          );
        }
        if (currentOrg?.id) {
          queryClient.invalidateQueries({
            queryKey: crmQueryKeys.objectDetail(
              currentOrg.id,
              'ahoy_research',
              object.entity_id
            ),
          });
        }
      } catch (err) {
        logger.error(
          'Failed to create email proposal from conversation starter',
          err instanceof Error ? err : undefined,
          {
            research_entity_id: object.entity_id,
            org_id: object.org_id,
            context_label: data.context_label,
            has_email: !!contactEmail,
          }
        );
        toast.error("We couldn't create the email draft. Try again.");
      }
    },
    [
      contactEmail,
      contactEntityId,
      recordType,
      user,
      object.org_id,
      object.entity_id,
      currentOrg?.id,
      createProposal,
      openProposal,
      queryClient,
    ]
  );

  const handleOpenProposal = useCallback(
    (proposalId: number) => {
      openProposal(proposalId);
    },
    [openProposal]
  );

  return (
    <CollapsibleSection title='Research brief' defaultExpanded>
      <ResearchDetail
        research={research}
        object={object}
        onComposeEmail={contactEmail ? handleComposeEmail : undefined}
        onOpenProposal={handleOpenProposal}
        isComposing={createProposal.isPending}
      />
    </CollapsibleSection>
  );
}
